import dayjs from 'dayjs';

/**
 * One vocabulary for how an order is described to a human, shared by the
 * admin order pages and the client order pages so a status or supplier never
 * has two names.
 */

export const ORDER_STATUS = {
  pending: { label: 'Processing', color: 'orange' },
  completed: { label: 'Completed', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
};

export function orderStatusLabel(status) {
  return ORDER_STATUS[status]?.label || String(status || '-');
}

export function orderStatusColor(status) {
  return ORDER_STATUS[status]?.color || 'default';
}

/** Supply source → short display name. Unknown providers fall back to their key. */
export const SOURCE_LABELS = {
  vouchain: 'Vouchain (internal)',
  iak: 'IAK',
  zhongmin: 'Zhongmin',
  gowabi: 'GoWabi',
  giftee: 'Giftee',
  external: 'External',
};

export const SOURCE_COLORS = {
  vouchain: 'blue',
  iak: 'volcano',
  zhongmin: 'magenta',
  gowabi: 'cyan',
  giftee: 'purple',
  external: 'default',
};

export function sourceLabel(source) {
  const key = String(source || '').toLowerCase();
  return SOURCE_LABELS[key] || (key ? key.toUpperCase() : '-');
}

export function sourceColor(source) {
  return SOURCE_COLORS[String(source || '').toLowerCase()] || 'default';
}

/**
 * Fulfillment shape, provider-agnostic. `kami` is a card/code, `url` a
 * redemption link, `zhichong` a direct top-up that never produces a code.
 */
export const FULFILLMENT_LABELS = {
  kami: 'Code',
  url: 'Link',
  zhichong: 'Direct top-up',
};

export function fulfillmentLabel(type) {
  const key = String(type || '').toLowerCase();
  return FULFILLMENT_LABELS[key] || null;
}

export function isDirectRechargeOrder(order) {
  const data = order?.externalOrderData || {};
  return (
    String(data.externalFulfillmentType || data.zhongminFulfillmentType || '').toLowerCase() ===
    'zhichong'
  );
}

/**
 * Everything an operator should notice about one order at a glance. Each
 * flag matches a backend `attention` queue so a badge in the table and a
 * filter in the toolbar always agree.
 */
export const STUCK_PENDING_MINUTES = 30;

export function orderAttentionFlags(order) {
  if (!order) return [];
  const data = order.externalOrderData || {};
  const flags = [];
  const isExternal = order.isExternalVoucherOrder === true;

  if (
    isExternal &&
    order.status === 'pending' &&
    (data.purchaseStage === 'uncertain' || data.providerSubmissionStatus === 'uncertain')
  ) {
    flags.push({
      key: 'uncertain',
      label: 'Supplier outcome unknown',
      color: 'gold',
      hint: 'The purchase was sent to the supplier but no answer was recorded. Verify in the supplier portal before doing anything.',
    });
  }
  if (data.providerSubmissionStatus === 'reconciling') {
    flags.push({ key: 'reconciling', label: 'Reconciling', color: 'processing' });
  }
  if (data.refund?.refundError || data.asyncMonitor?.lastRefundError || /auto-refund failed/i.test(String(order.failureReason || ''))) {
    flags.push({
      key: 'refund_failed',
      label: 'Refund failed',
      color: 'red',
      hint: 'The client was charged and the automatic refund did not go through.',
    });
  } else if (order.refunded || data.refund?.refundedAt) {
    flags.push({ key: 'refunded', label: 'Refunded', color: 'purple' });
  }
  if (
    order.status === 'pending' &&
    order.createdAt &&
    dayjs().diff(dayjs(order.createdAt), 'minute') >= STUCK_PENDING_MINUTES &&
    !flags.some((f) => f.key === 'uncertain')
  ) {
    flags.push({
      key: 'stuck_pending',
      label: `Pending > ${STUCK_PENDING_MINUTES} min`,
      color: 'orange',
      hint: 'Still waiting on the supplier. Check the async monitor and the supplier portal.',
    });
  }
  return flags;
}

/** The single most useful short description of why an order is in its state. */
export function orderStatusDescription(order) {
  if (!order) return '';
  const data = order.externalOrderData || {};
  if (order.status === 'failed') {
    return (
      order.failureReason ||
      data.failReason ||
      data.fulfillmentSummary?.failureReason ||
      'The order failed.'
    );
  }
  if (order.status === 'pending') {
    if (data.purchaseStage === 'uncertain') return 'Sent to the supplier; outcome not yet confirmed.';
    if (order.isExternalVoucherOrder) return 'Waiting for the supplier to fulfil this order.';
    return 'Order is being processed.';
  }
  if (order.status === 'completed') {
    return isDirectRechargeOrder(order) ? 'Direct top-up credited.' : 'Fulfilled.';
  }
  return '';
}

export const PURCHASE_STAGE_LABELS = {
  created: 'Created (not yet charged)',
  paid: 'Charged, not yet sent to supplier',
  uncertain: 'Sent to supplier — outcome unknown',
  acknowledged: 'Supplier acknowledged',
  failed: 'Failed',
};

export const SUBMISSION_STATUS_LABELS = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  uncertain: 'Uncertain',
  reconciling: 'Reconciling (manual)',
  confirmed_failed: 'Confirmed failed',
};

export const MAX_PURCHASE_ATTEMPTS = 3;
